import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Box,
  Divider,
  IconButton,
  InputAdornment,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import type { FC } from 'react';
import React, { useCallback, useRef, useState } from 'react';
import doFetch from 'utils/api/doFetch';

import pageUrls from 'constants/pageUrls';
import { validEmailRegex } from 'constants/regex';

import Button from 'components/Button';
import FilledTextField from 'components/MuiCustom/FilledTextField';

import { useAppDispatch, useAppSelector } from 'redux-store';
import { closeSidebar } from 'redux-store/slices/toggleSidebarMenu';
import { setLoginTransition } from 'redux-store/slices/ui';

const LoginPage: FC = () => {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const lgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const isSidebarOpen = useAppSelector(state => state.sidebar.isOpen);

  const emailRef = useRef<HTMLInputElement>(null);

  const [emailTouched, setEmailTouched] = useState<boolean>(false);

  const passwordRef = useRef<HTMLInputElement>(null);

  const [isLoading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [genericError, setGenericError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | true | null>(null);
  const [passwordError, setPasswordError] = useState<string | true | null>(
    null,
  );

  const doLogin = useCallback(
    async (event: { preventDefault: () => void }) => {
      event.preventDefault();
      let error = false;

      const email = emailRef.current?.value.toLowerCase() || '';
      const password = passwordRef.current?.value || '';

      if (email.length < 1 || !validEmailRegex.test(email)) {
        setEmailError(t('common:login_errors.enter_valid_email'));
        setEmailTouched(false);
        error = true;
      }

      if (password.length < 1) {
        setPasswordError(t('common:login_errors.enter_password'));
        error = true;
      }

      if (error) return;

      setLoading(true);
      try {
        const apiPointName = 'login';

        const result = await doFetch<typeof apiPointName>({
          apiPointName,
          body: {
            email,
            password,
          },
        });

        if (!result.success) {
          throw new Error(result.error);
        }

        dispatch(setLoginTransition(true));

        if (!lgUp && isSidebarOpen) {
          dispatch(closeSidebar());
        }

        await router.push(pageUrls.homeDashboard, undefined, {
          locale: router.locale,
        });
      } catch (e) {
        const err = e as { message?: string; name?: string; code?: string };

        // handle error
        if (err.message) {
          if (i18n.exists(err.message)) {
            setGenericError(t(err.message as string) as string);
          } else {
            // TODO: check this
            // console.log(
            //   `Error message "${err.message}" not found in translations`,
            // );
            setGenericError(err.message);
          }
        } else {
          setGenericError(t('common:something_went_wrong'));
        }
        setLoading(false);
      }
    },
    [t, dispatch, lgUp, isSidebarOpen, router, i18n],
  );

  const handleAnonymousLogin = useCallback(async () => {
    setLoading(true);
    try {
      const apiPointName = 'loginAnonymous';

      const result = await doFetch<typeof apiPointName>({
        apiPointName,
      });

      if (!result.success) {
        throw new Error(result.error);
      }

      dispatch(setLoginTransition(true));

      await router.push(pageUrls.homeDashboard, undefined, {
        locale: router.locale,
      });
    } catch (e) {
      const err = e as { message?: string; name?: string; code?: string };

      // handle error
      if (err.message) {
        if (i18n.exists(err.message)) {
          setGenericError(t(err.message as string) as string);
        } else {
          // TODO: check this
          // console.log(
          //   `Error message "${err.message}" not found in translations`,
          // );
          setGenericError(err.message);
        }
      } else {
        setGenericError(t('common:something_went_wrong'));
      }
      setLoading(false);
    }
  }, [dispatch, i18n, router, t]);

  const handleErrorReset = useCallback(() => {
    if (genericError && (emailError || passwordError)) {
      setEmailError(null);
      setPasswordError(null);
      setGenericError(null);
    }
  }, [genericError, emailError, passwordError]);

  const handleEmailChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEmailTouched(true);
      handleErrorReset();
      const email = event.target.value;

      if ((email.length > 0 || emailTouched) && !validEmailRegex.test(email)) {
        setEmailError(t('common:login_errors.enter_valid_email'));
      } else {
        setEmailError(null);
      }
    },
    [emailTouched, handleErrorReset, t],
  );

  const handlePasswordChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleErrorReset();
      const password = event.target.value;

      if (password.length > 0) {
        setPasswordError(null);
      }
    },
    [handleErrorReset],
  );

  return (
    <>
      <Typography variant="h1" mb={1} align="center">
        {t('common:login')}
      </Typography>
      <Typography variant="body1" align="center" mb={2}>
        {t('common:login_bottom_text')}
      </Typography>
      <Box mb={2}>
        <form onSubmit={doLogin}>
          <Box mb={2}>
            <Typography
              variant="body2"
              color="error"
              style={{ opacity: typeof genericError === 'string' ? 1 : 0 }}
              align="left"
            >
              {typeof genericError === 'string' ? genericError : '_'}
            </Typography>
          </Box>
          <Box mb={3} width="100%">
            <FilledTextField
              inputRef={emailRef}
              id="login-email"
              label={t('common:email') as string}
              onChange={handleEmailChange}
              autoComplete="email"
              required
              error={!!emailError}
              helperText={
                typeof emailError === 'string' ? emailError : undefined
              }
              fullWidth
            />
          </Box>
          <Box mb={2} width="100%">
            <FilledTextField
              inputRef={passwordRef}
              id="login-password"
              label={t('common:password') as string}
              onChange={handlePasswordChange}
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              error={!!passwordError}
              helperText={
                typeof passwordError === 'string' ? passwordError : undefined
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={(): void => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              fullWidth
            />
          </Box>
          <Box mb={3} width="100%">
            <Typography
              variant="body1"
              textAlign="left"
              pl={1}
              fontWeight={600}
            >
              <Link
                href={pageUrls.forgotPassword}
                data-testid="forgot-password-link"
                style={{
                  color: theme.vars.palette.primary.main,
                }}
              >
                {t('common:create_account.forgot_password.title')}
              </Link>
            </Typography>
          </Box>
          <Box width="100%">
            <Button
              variant="flat"
              fullWidth
              color="primary"
              type="submit"
              size="large"
              disabled={isLoading || !!emailError || !!passwordError}
              onClick={doLogin}
              data-testid="submit"
            >
              {t('common:login')}
            </Button>
          </Box>
        </form>
      </Box>
      <Box mb={2}>
        <Typography variant="body1" align="center">
          {t('common:no_account.pre_text')}{' '}
          <Link
            href={pageUrls.register}
            data-testid="signup-link"
            style={{
              color: theme.vars.palette.primary.main,
              fontWeight: '600',
            }}
          >
            {t('common:no_account.link_text')}
          </Link>
        </Typography>
      </Box>
      <Divider>{t('common:or')}</Divider>
      <Box mt={2}>
        <Button
          variant="text"
          fullWidth
          color="primary"
          size="large"
          onClick={handleAnonymousLogin}
          data-testid="login-anonymously"
        >
          {t('common:login_anonymously')}
        </Button>
      </Box>
    </>
  );
};

export default LoginPage;
